/**
 * authSetup.ts
 *
 * App.vue から認証・教員/生徒同期関連のロジックを切り出したモジュール。
 * すべての関数は vm（App.vue の this）を第1引数に受け取る。
 */

// @ts-ignore
import firebase from 'firebase/app'
// @ts-ignore
import { db } from '@/firebase'
// @ts-ignore
import axios from 'axios'
import { VPS_API_BASE } from '@/js/phpApi'
import { requestAddLayers } from '@/js/layerEngine/addLayersGate'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VM = any

// ============================================================
// 純粋関数（this 不要）
// ============================================================

/** URL比較（sパラメータで判定） */
export function isSameUrl(url1: string, url2: string): boolean {
  try {
    const u1 = new URL(url1)
    const u2 = new URL(url2)
    const s1 = u1.searchParams.get('s') || ''
    const s2 = u2.searchParams.get('s') || ''
    return s1 === s2 && s1 !== ''
  } catch {
    return url1 === url2
  }
}

/** ドローレイヤーのopacityを一括設定（フェード効果用） */
export function setDrawLayersOpacity(map: unknown, opacity: number): void {
  const m = map as any // eslint-disable-line @typescript-eslint/no-explicit-any
  const opacitySettings = [
    ['click-circle-layer', 'fill-opacity'],
    ['click-circle-polygon-line-layer', 'line-opacity'],
    ['click-circle-polygon-symbol-layer', 'text-opacity'],
    ['click-circle-polygon-symbol-area-layer', 'text-opacity'],
    ['click-circle-line-layer', 'line-opacity'],
    ['click-circle-keiko-line-layer', 'line-opacity'],
    ['click-circle-memo-mark-layer', 'text-opacity'],
    ['click-circle-label-layer', 'text-opacity'],
    ['click-circle-center-label-layer', 'text-opacity'],
    ['click-circle-symbol-layer', 'circle-opacity'],
    ['arrows-endpoint-layer', 'icon-opacity'],
    ['segment-label-layer', 'text-opacity']
  ]
  opacitySettings.forEach(([layerId, prop]) => {
    if (m.getLayer(layerId)) {
      m.setPaintProperty(layerId, prop, opacity)
    }
  })
}

/** 同期タイムアウトチェック（30分で自動解除） */
export function checkSyncTimeout(lesson: any): boolean { // eslint-disable-line @typescript-eslint/no-explicit-any
  if (!lesson.syncEnabled || !lesson.syncEnabledAt) {
    return false
  }

  const SYNC_TIMEOUT_MS = 30 * 60 * 1000 // 30分
  const now = Date.now()

  let syncEnabledTime: number
  if (lesson.syncEnabledAt.toDate) {
    syncEnabledTime = lesson.syncEnabledAt.toDate().getTime()
  } else if (lesson.syncEnabledAt instanceof Date) {
    syncEnabledTime = lesson.syncEnabledAt.getTime()
  } else {
    syncEnabledTime = new Date(lesson.syncEnabledAt).getTime()
  }

  const elapsed = now - syncEnabledTime
  return elapsed > SYNC_TIMEOUT_MS
}

// ============================================================
// VM 依存関数
// ============================================================

/** 海外からのアクセスを検出 */
export async function checkOverseas(vm: VM): Promise<void> {
  try {
    const res = await fetch('https://ipapi.co/country_code/', { signal: AbortSignal.timeout(3000) } as any) // eslint-disable-line @typescript-eslint/no-explicit-any
    const code = await res.text()
    vm.$store.state.isOverseas = code.trim() !== 'JP'
    if (vm.$store.state.isOverseas) {
    }
  } catch {
    vm.$store.state.isOverseas = false
  }
}

/** 認証チェック用のfetchヘルパー（個別のエラーハンドリング付き） */
async function fetchJson(url: string, body: Record<string, unknown>, label: string): Promise<any> { // eslint-disable-line @typescript-eslint/no-explicit-any
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    return await res.json()
  } catch (e) {
    console.error(`[${label}] チェックエラー:`, e)
    return null
  }
}

/** 教員・生徒チェック（4つのAPIを並列呼び出し） */
export async function checkTeacherStatus(vm: VM, uid: string): Promise<void> {
  if (!uid) return

  // 4つのAPIを同時に呼び出し（逐次→並列で体感速度向上）
  const [teacherData, studentData, orgOwnerData, orgMemberData] = await Promise.all([
    fetchJson('/oh3/php/teacher_check.php', { uid }, 'Teacher'),
    fetchJson('/oh3/php/student_classes.php', { firebase_uid: uid }, 'Student'),
    fetchJson('/oh3/php/org_check.php', { uid }, 'Org'),
    fetchJson('/oh3/php/org_member_check.php', { uid }, 'Org')
  ])

  // 教員チェック
  if (teacherData?.status === 'success' && teacherData.isTeacher) {
    vm.$store.state.isTeacher = true
    vm.$store.state.teacherInfo = teacherData.teacher
    startTeacherLessonSync(vm, uid)
  } else {
    vm.$store.state.isTeacher = false
    vm.$store.state.teacherInfo = null
  }

  // 生徒クラス所属チェック
  if (studentData?.status === 'success' && studentData.classes?.length > 0) {
    vm.$store.state.isStudent = true
    vm.$store.state.studentClasses = studentData.classes
    startLessonListener(vm, studentData.classes)
  } else {
    vm.$store.state.isStudent = false
    vm.$store.state.studentClasses = []
  }

  // 組織オーナーチェック
  if (orgOwnerData?.status === 'success' && orgOwnerData.isOrganizationOwner) {
    vm.$store.state.isOrganizationOwner = true
    vm.$store.state.organizationInfo = orgOwnerData.organization || orgOwnerData.admin
    vm.$store.state.orgGroups = orgOwnerData.groups || []
  } else {
    vm.$store.state.isOrganizationOwner = false
    vm.$store.state.organizationInfo = null
  }

  // 組織メンバーチェック
  if (orgMemberData?.status === 'success' && orgMemberData.isOrganizationMember) {
    vm.$store.state.isOrganizationMember = true
    vm.$store.state.organizationMemberInfo = orgMemberData.member
    // メンバーの所属グループ一覧をセット（オーナーで既にセットされていない場合のみ）
    if (!vm.$store.state.isOrganizationOwner && orgMemberData.member?.groups) {
      vm.$store.state.orgGroups = orgMemberData.member.groups
    }
  } else {
    vm.$store.state.isOrganizationMember = false
    vm.$store.state.organizationMemberInfo = null
  }
}

/** 授業モードリスナー（生徒側） */
export function startLessonListener(vm: VM, classes: any[]): void { // eslint-disable-line @typescript-eslint/no-explicit-any
  if (!classes || classes.length === 0) return

  const classIds = classes.map((c: any) => Number(c.class_id)) // eslint-disable-line @typescript-eslint/no-explicit-any

  classIds.forEach((classId: number) => {
    db.collection('lessons').doc(String(classId)).onSnapshot((doc: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
      if (!doc.exists) return

      const lesson = doc.data()

      if (lesson.active) {
        vm.$store.state.lessonMode = {
          active: true,
          syncEnabled: lesson.syncEnabled,
          className: lesson.className,
          teacherName: '',
          classId: classId,
          pendingViewState: null
        }

        const isSyncTimedOut = checkSyncTimeout(lesson)
        const effectiveSyncEnabled = lesson.syncEnabled && !isSyncTimedOut
        if (vm.$store.state.lessonMode) {
          vm.$store.state.lessonMode.syncEnabled = effectiveSyncEnabled
        }

        if (effectiveSyncEnabled) {
          if (vm.$store.state.map01) {
            applyLessonFullState(vm, lesson)
          } else {
            vm.$store.state.lessonMode.pendingLessonData = lesson
          }
        }
      } else {
        if (vm.$store.state.lessonMode.active && vm.$store.state.lessonMode.classId === classId) {
          vm.$store.state.lessonMode = {
            active: false,
            syncEnabled: false,
            className: '',
            teacherName: '',
            classId: null
          }
        }
      }
    }, (error: Error) => {
      console.error('[Lesson] リスナーエラー:', error)
    })
  })
}

/** 教員用の授業同期（ダッシュボードを閉じても動作） */
export async function startTeacherLessonSync(vm: VM, teacherUid: string): Promise<void> {
  try {
    const snapshot = await db.collection('lessons')
      .where('teacherUid', '==', teacherUid)
      .where('active', '==', true)
      .limit(1)
      .get()

    if (snapshot.empty) return

    const doc = snapshot.docs[0]
    const lessonData = doc.data()
    const classId = lessonData.classId

    vm.$store.state.activeLesson = {
      classId,
      syncEnabled: lessonData.syncEnabled,
      teacherUid
    }

    if (lessonData.syncEnabled) {
      startTeacherMapSync(vm, classId)
    }

    vm.teacherLessonUnsubscribe = db.collection('lessons').doc(String(classId)).onSnapshot((doc: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
      if (!doc.exists) {
        stopTeacherMapSync(vm)
        vm.$store.state.activeLesson = null
        return
      }

      const lesson = doc.data()
      if (!lesson.active) {
        stopTeacherMapSync(vm)
        vm.$store.state.activeLesson = null
        return
      }

      const currentSync = vm.$store.state.activeLesson?.syncEnabled
      if (lesson.syncEnabled !== currentSync) {
        vm.$store.state.activeLesson.syncEnabled = lesson.syncEnabled
        if (lesson.syncEnabled) {
          startTeacherMapSync(vm, classId)
        } else {
          stopTeacherMapSync(vm)
        }
      }
    })

  } catch (e) {
    console.error('[TeacherSync] エラー:', e)
  }
}

/** 教員のマップ同期を開始 */
export function startTeacherMapSync(vm: VM, classId: number): void {
  stopTeacherMapSync(vm)

  const map = vm.$store.state.map01
  if (!map) {
    const checkMap = setInterval(() => {
      if (vm.$store.state.map01) {
        clearInterval(checkMap)
        startTeacherMapSync(vm, classId)
      }
    }, 100)
    setTimeout(() => clearInterval(checkMap), 10000)
    return
  }

  let sending = false
  let initialSent = false

  const sendFullState = async () => {
    const activeLesson = vm.$store.state.activeLesson
    if (!activeLesson || !activeLesson.syncEnabled) return
    if (sending) return

    const viewState = {
      center: [map.getCenter().lng, map.getCenter().lat],
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch()
    }
    const selectedLayers = vm.$store.state.selectedLayers || {}
    const isTerrainEnabled = vm.$store.state.isTerrainEnabled || false
    const clickCircleGeojsonText = vm.$store.state.clickCircleGeojsonText || ''

    try {
      sending = true
      await db.collection('lessons').doc(String(classId)).update({
        viewState,
        selectedLayersJson: JSON.stringify(selectedLayers),
        isTerrainEnabled,
        clickCircleGeojsonText,
        updatedAt: new Date()
      })
    } catch (e) {
      console.error('[TeacherSync] 送信エラー:', e)
    } finally {
      sending = false
    }
  }

  let layerSyncTimer: ReturnType<typeof setTimeout> | null = null
  const debouncedLayerSync = () => {
    if (layerSyncTimer) clearTimeout(layerSyncTimer)
    layerSyncTimer = setTimeout(sendFullState, 300)
  }

  map.on('moveend', sendFullState)

  vm.teacherLayerWatcher = vm.$watch(
    () => vm.$store.state.selectedLayers,
    debouncedLayerSync,
    { deep: true }
  )

  vm.teacherTerrainWatcher = vm.$watch(
    () => vm.$store.state.isTerrainEnabled,
    sendFullState
  )

  vm.teacherDrawWatcher = vm.$watch(
    () => vm.$store.state.clickCircleGeojsonText,
    debouncedLayerSync
  )

  vm.teacherMapSyncHandler = sendFullState

  if (!initialSent) {
    initialSent = true
    sendFullState()
    setTimeout(() => { sendFullState() }, 500)
    setTimeout(() => { sendFullState() }, 2000)
  }
}

/** 教員のマップ同期を停止 */
export function stopTeacherMapSync(vm: VM): void {
  if (vm.teacherMapSyncHandler && vm.$store.state.map01) {
    vm.$store.state.map01.off('moveend', vm.teacherMapSyncHandler)
    vm.teacherMapSyncHandler = null
  }
  if (vm.teacherLayerWatcher) {
    vm.teacherLayerWatcher()
    vm.teacherLayerWatcher = null
  }
  if (vm.teacherTerrainWatcher) {
    vm.teacherTerrainWatcher()
    vm.teacherTerrainWatcher = null
  }
  if (vm.teacherDrawWatcher) {
    vm.teacherDrawWatcher()
    vm.teacherDrawWatcher = null
  }
}

/** 生徒の地図操作を無効化（授業同期ON時） */
export function disableStudentMapInteractions(vm: VM): void {
  const map = vm.$store.state.map01
  if (!map) return

  map.dragPan.disable()
  map.scrollZoom.disable()
  map.doubleClickZoom.disable()
  map.touchZoomRotate.disable()
  map.keyboard.disable()
  vm.studentInteractionsDisabled = true
  vm.$store.state.studentSyncLocked = true
}

/** 生徒の地図操作を有効化（授業同期OFF時または授業終了時） */
export function enableStudentMapInteractions(vm: VM): void {
  const map = vm.$store.state.map01
  if (!map) return

  map.dragPan.enable()
  map.scrollZoom.enable()
  map.doubleClickZoom.enable()
  map.touchZoomRotate.enable()
  map.keyboard.enable()
  vm.studentInteractionsDisabled = false
  vm.$store.state.studentSyncLocked = false
}

/** 先生のビュー状態を適用 */
export function applyLessonViewState(vm: VM, viewState: any): void { // eslint-disable-line @typescript-eslint/no-explicit-any
  if (!viewState || !vm.$store.state.map01) return

  const map = vm.$store.state.map01
  const centerLng = Number(viewState.center?.[0])
  const centerLat = Number(viewState.center?.[1])
  const zoomNum = Number(viewState.zoom)
  const bearingNum = Number(viewState.bearing) || 0
  const pitchNum = Number(viewState.pitch) || 0

  if (isNaN(centerLng) || isNaN(centerLat) || isNaN(zoomNum)) return

  map.jumpTo({
    center: [centerLng, centerLat],
    zoom: zoomNum,
    bearing: bearingNum,
    pitch: pitchNum
  })
}

/** 先生の完全な状態を適用（viewState + selectedLayers + isTerrainEnabled） */
export function applyLessonFullState(vm: VM, lessonData: any): void { // eslint-disable-line @typescript-eslint/no-explicit-any
  const map = vm.$store.state.map01
  if (!map) return

  // 1. ビュー状態を適用
  if (lessonData.viewState) {
    applyLessonViewState(vm, lessonData.viewState)
  }

  // 2. レイヤーを適用
  if (lessonData.selectedLayersJson) {
    try {
      const selectedLayers = JSON.parse(lessonData.selectedLayersJson)
      requestAddLayers(vm.$store, 'map01')
      vm.$store.state.selectedLayers = selectedLayers
    } catch (e) {
      console.error('[Lesson] レイヤーJSON解析エラー:', e)
    }
  }

  // 3. 3D地形を適用
  if (lessonData.isTerrainEnabled !== undefined) {
    const currentTerrain = vm.$store.state.isTerrainEnabled
    if (currentTerrain !== lessonData.isTerrainEnabled) {
      vm.$store.state.isTerrainEnabled = lessonData.isTerrainEnabled
      if (lessonData.isTerrainEnabled) {
        const source = map.getSource('terrain') ? 'terrain' : 'terrain-source'
        map.setTerrain({ source, exaggeration: vm.$store.state.terrainLevel || 1.5 })
      } else {
        map.setTerrain(null)
      }
    }
  }

  // 4. ドローを適用
  if (lessonData.clickCircleGeojsonText !== undefined) {
    const currentDraw = vm.$store.state.clickCircleGeojsonText || ''
    if (currentDraw !== lessonData.clickCircleGeojsonText) {
      vm.$store.state.clickCircleGeojsonText = lessonData.clickCircleGeojsonText
      applyDrawToMap(vm, map, lessonData.clickCircleGeojsonText, 0)
    }
  }
}

/** ドローを地図に適用（リトライ機構付き） */
export function applyDrawToMap(vm: VM, map: any, clickCircleGeojsonText: string, retryCount: number): void { // eslint-disable-line @typescript-eslint/no-explicit-any
  const maxRetries = 5
  const source = map.getSource('click-circle-source')

  if (!source) {
    if (retryCount < maxRetries) {
      setTimeout(() => {
        applyDrawToMap(vm, map, clickCircleGeojsonText, retryCount + 1)
      }, 500)
    } else {
      console.warn('[Lesson] click-circle-source が作成されませんでした')
    }
    return
  }

  try {
    setDrawLayersOpacity(map, 0.3)

    setTimeout(() => {
      if (clickCircleGeojsonText) {
        const geojson = JSON.parse(clickCircleGeojsonText)
        source.setData(geojson)
      } else {
        source.setData({ type: 'FeatureCollection', features: [] })
      }
      setTimeout(() => {
        setDrawLayersOpacity(map, vm.$store.state.drawOpacity || 1)
      }, 50)
    }, 100)
  } catch (e) {
    console.error('[Lesson] ドロー適用エラー:', e)
  }
}

/** ログインユーザーのDB設定から状態を復元 */
export async function restoreUserSettings(vm: VM, { alertOnMissing = false } = {}): Promise<void> {
  try {
    const notifyMissingConfig = () => {
      if (!alertOnMissing) return
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert('保存された設定値が見つかりません。')
      }
    }

    const uidFromStore = vm.$store?.state?.userId
    const uidFromLocalStorage = localStorage.getItem('lastUserId')
    const uid = uidFromStore || uidFromLocalStorage
    if (!uid) {
      console.warn('[restoreUserSettings] ユーザーIDが見つかりません。復元処理をスキップします。')
      notifyMissingConfig()
      return
    }


    const configResponse = await axios.get('/oh3/php/userConfigSelect.php', {
      params: { uid: uid }
    })

    if (configResponse.data.error) {
      console.error('[restoreUserSettings] ユーザー設定取得エラー:', configResponse.data.error)
      notifyMissingConfig()
      return
    }

    const config = Array.isArray(configResponse.data) ? configResponse.data[0] : null
    const starturl = config?.starturl ?? ''

    if (!starturl) {
      console.warn('[restoreUserSettings] starturl未設定のため、復元処理をスキップします')
      notifyMissingConfig()
      return
    }

    const urlResponse = await axios.get(`${VPS_API_BASE}/shortUrlSelect.php`, {
      params: { urlid: starturl },
      timeout: 5000,
    })

    if (urlResponse.data.error) {
      console.error('[restoreUserSettings] URL取得エラー:', urlResponse.data.error)
      notifyMissingConfig()
      return
    }

    if (!urlResponse.data) {
      console.warn('[restoreUserSettings] 短縮URLのパラメータが空です')
      notifyMissingConfig()
      return
    }

    const params = new URL('https://dummy/&' + urlResponse.data).searchParams
    await vm.restoreFromParamString(params.toString())

  } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    console.error('[restoreUserSettings] エラー:', error)
    console.error('[restoreUserSettings] エラー詳細:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    })
    if (alertOnMissing) {
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert('設定の復元中にエラーが発生しました。')
      }
    }
  }
}

/** Firebase認証状態リスナーのセットアップ */
export function setupAuthStateListener(vm: VM): void {
  firebase.auth().onAuthStateChanged((user: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    vm.authStateReady = true

    if (user) {
      checkTeacherStatus(vm, user.uid)
    } else {
      vm.$store.state.isTeacher = false
      vm.$store.state.teacherInfo = null
      vm.$store.state.isStudent = false
      vm.$store.state.studentClasses = []
      vm.$store.state.isOrganizationOwner = false
      vm.$store.state.organizationInfo = null
      vm.$store.state.isOrganizationMember = false
      vm.$store.state.organizationMemberInfo = null
      vm.$store.state.orgGroups = []
    }
  })
}

/**
 * 組織グループ一覧を再取得してstoreを更新
 * GroupManageDialogでグループ作成/削除/名前変更後に呼ぶ
 */
export async function refreshOrgGroups(vm: VM): Promise<void> {
  const uid = vm.$store.state.userId
  if (!uid) return

  try {
    if (vm.$store.state.isOrganizationOwner) {
      const res = await fetch('/oh3/php/org_check.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid })
      })
      const data = await res.json()
      if (data.status === 'success' && data.groups) {
        vm.$store.state.orgGroups = data.groups
      }
    } else if (vm.$store.state.isOrganizationMember) {
      const res = await fetch('/oh3/php/org_member_check.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid })
      })
      const data = await res.json()
      if (data.status === 'success' && data.member?.groups) {
        vm.$store.state.orgGroups = data.member.groups
      }
    }
  } catch (e) {
    console.error('[Org] グループリスト更新エラー:', e)
  }
}
